import { useContext } from "react";

import { AppConfigContext } from "../../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../../core/data-access/useAuthorizedFetch";
import { ChangeFileRecordToQcDoneRequest } from "./change-to-qc-done/ChangeFileRecordToQcDoneRequest";
import { CreateFileRecordResponse } from "./create/CereateFileRecordResponse";
import { CreateAudioFileRecordRequest } from "./create/CreateAudioFileRecordRequest";
import { CreateSubtitleFileRecordRequest } from "./create/CreateSubtitleFileRecordRequest";
import { CreateVideoFileRecordRequest } from "./create/CreateVideoFileRecordRequest";
import { DestroyFileRecordRequest } from "./destroy/DestroyFileRecordRequest";
import { GetFileRecordDetailsRequest } from "./details/GetFileRecordDetailsRequest";
import { GetFileRecordDetailsResponse } from "./details/GetFileRecordDetailsResponse";
import { EnableFileRecordNotificationsRequest } from "./enable-notifications/EnableFileRecordNotificationsRequest";
import { GetFileRecordListRequest } from "./get-list/GetFileRecordListRequest";
import { GetFileRecordListResponse } from "./get-list/GetFileRecordListResponse";
import { RemoveFileRecordRequest } from "./remove/RemoveFileRecordRequest";
import { ChangeFileRecordToQcRequest } from "./status/change-to-editing/ChangeFileRecordToEditingRequest";
import { ChangeFileRecordToEditingRequest } from "./status/change-to-qc/ChangeFileRecordToQcRequest";
import { ChangeFileRecordToWaitingForClientResponseRequest } from "./status/change-to-waiting-for-client-response/ChangeFileRecordToWaitingForClientResponseRequest";
import { RejectFileRecordRequest } from "./status/reject/RejectFileRecordRequest";
import { UpdateFileRecordContentRequest } from "./update/content/UpdateFileRecordContentRequest";
import { UpdateFileRecordLibraryRequest } from "./update/library/UpdateFileRecordLibraryRequest";
import { OverrideFileRecordWithCloudFileRequest } from "./update/override/OverrideFileRecordWithCloudFileRequest";
import { UpdateFileRecordAudioSoundtrackRequest } from "./update/soundtrack/UpdateFileRecordAudioSoundtrackRequest";
import { UpdateFileRecordVideoSoundtracksRequest } from "./update/soundtrack/UpdateFileRecordVideoSoundtracksRequest";
import { UpdateFileRecordSubtitleRequest } from "./update/subtitle/UpdateFileRecordSubtitleRequest";
import { UpdateFileRecordAudioTechnicalRequest } from "./update/technical/audio/UpdateFileRecordAudioTechnicalRequest";
import { UpdateFileRecordVideoTechnicalRequest } from "./update/technical/video/UpdateFileRecordVideoTechnicalRequest";
import { ValidateFileRecordToQcDoneRequest } from "./validate-to-qc-done/ValidateFileRecordToQcDoneRequest";
import { ValidateFileRecordToQcDoneResponse } from "./validate-to-qc-done/ValidateFileRecordToQcDoneResponse";

export const useFileRecordApi = () => {
    const appConfigContext = useContext(AppConfigContext);
    const baseUrl = `${appConfigContext.masterConfig.apiUrl}/file-records`;

    const { httpGet, httpPost, httpPut, httpDelete } = useAuthorizedFetch();

    const getFileRecordsByQuery = async (query: GetFileRecordListRequest): Promise<GetFileRecordListResponse> => {
        return httpGet<GetFileRecordListResponse>(`${baseUrl}/get-list`, query);
    };

    const getFileRecordDetails = async (query: GetFileRecordDetailsRequest): Promise<GetFileRecordDetailsResponse> => {
        return httpGet<GetFileRecordDetailsResponse>(`${baseUrl}/get-details`, query);
    };

    const createVideoFileRecord = async (request: CreateVideoFileRecordRequest): Promise<CreateFileRecordResponse> => {
        return httpPost(`${baseUrl}/create-video`, request);
    };

    const createAudioFileRecord = async (request: CreateAudioFileRecordRequest): Promise<CreateFileRecordResponse> => {
        return httpPost(`${baseUrl}/create-audio`, request);
    };

    const createSubtitleFileRecord = async (
        request: CreateSubtitleFileRecordRequest
    ): Promise<CreateFileRecordResponse> => {
        return httpPost(`${baseUrl}/create-subtitle`, request);
    };
    const approveFileRecordNotifications = async (request: EnableFileRecordNotificationsRequest): Promise<void> => {
        return httpPut(`${baseUrl}/approve-notifications`, request);
    };

    const rejectFileRecord = async (request: RejectFileRecordRequest): Promise<void> => {
        return httpPut(`${baseUrl}/reject`, request);
    };

    const destroyFileRecord = async (request: DestroyFileRecordRequest): Promise<void> => {
        return httpPut(`${baseUrl}/destroy`, request);
    };

    const changeFileRecordToEditing = async (request: ChangeFileRecordToEditingRequest): Promise<void> => {
        return httpPut(`${baseUrl}/change-to-editing`, request);
    };

    const changeFileRecordToQcDone = async (request: ChangeFileRecordToQcDoneRequest): Promise<void> => {
        return httpPut(`${baseUrl}/change-to-qc-done`, request);
    };

    const changeFileRecordToQc = async (request: ChangeFileRecordToQcRequest): Promise<void> => {
        return httpPut(`${baseUrl}/change-to-qc`, request);
    };

    const changeFileRecordToWaitingForClientResponse = async (
        request: ChangeFileRecordToWaitingForClientResponseRequest
    ): Promise<void> => {
        return httpPut(`${baseUrl}/change-to-waiting-for-client-response`, request);
    };

    const removeFileRecord = async (request: RemoveFileRecordRequest): Promise<void> => {
        return httpDelete(`${baseUrl}/remove`, request);
    };

    const updateFileRecordLibrary = async (request: UpdateFileRecordLibraryRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-library`, request);
    };

    const updateFileRecordContent = async (request: UpdateFileRecordContentRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-content`, request);
    };

    const updateFileRecordAudioSoundtrack = async (request: UpdateFileRecordAudioSoundtrackRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-audio-soundtrack`, request);
    };

    const updateFileRecordVideoSoundtracks = async (
        request: UpdateFileRecordVideoSoundtracksRequest
    ): Promise<void> => {
        return httpPut(`${baseUrl}/update-video-soundtracks`, request);
    };

    const updateFileRecordSubtitle = async (request: UpdateFileRecordSubtitleRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-subtitle`, request);
    };

    const updateFileRecordVideoTechnical = async (request: UpdateFileRecordVideoTechnicalRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-video-technical`, request);
    };

    const updateFileRecordAudioTechnical = async (request: UpdateFileRecordAudioTechnicalRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-audio-technical`, request);
    };

    const overrideFileRecordWithCloudFile = async (request: OverrideFileRecordWithCloudFileRequest): Promise<void> => {
        return httpPut(`${baseUrl}/override-with-cloud-file`, request);
    };

    const validateFileRecordToQcDone = async (
        query: ValidateFileRecordToQcDoneRequest
    ): Promise<ValidateFileRecordToQcDoneResponse> => {
        return httpGet<ValidateFileRecordToQcDoneResponse>(`${baseUrl}/validate-to-qc-done`, query);
    };

    return {
        getFileRecordsByQuery,
        createVideoFileRecord,
        createSubtitleFileRecord,
        createAudioFileRecord,
        getFileRecordDetails,
        approveFileRecordNotifications,
        rejectFileRecord,
        destroyFileRecord,
        changeFileRecordToEditing,
        changeFileRecordToQcDone,
        changeFileRecordToQc,
        changeFileRecordToWaitingForClientResponse,
        removeFileRecord,
        updateFileRecordLibrary,
        updateFileRecordContent,
        updateFileRecordAudioSoundtrack,
        updateFileRecordVideoSoundtracks,
        updateFileRecordSubtitle,
        updateFileRecordVideoTechnical,
        updateFileRecordAudioTechnical,
        overrideFileRecordWithCloudFile,
        validateFileRecordToQcDone
    };
};
